import type { RouterContextType } from "@/api/permissions/permissions.api";
import { SERVICE_PROVIDER } from "@/components/data/constants";
import { getActiveSessionState } from "@/hooks/useActiveSession";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { useLogout } from "@/utils/authHelpers";
import { Box, Group, Image, Stack, Text } from "@mantine/core";
import { Button, Card } from "@onengine/component-library";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import terminalStyles from "@/routes/sp-invite/review/terminal-screen.module.scss";
import styles from "./SpHome.module.scss";

const HEADER_TEXT = "Service providers don't sign in here";
const NOTHING_HERE_TEXT =
  "We'll text you a link when there's work for you, and it opens without signing in.";
const CONTENT_GAP = "var(--spacing-x-large, 24px)";

// SERV-3097: the one page a leftover service-provider session has. Provider
// logins are refused, so this only offers to log out.
export const Route = createFileRoute("/sp-home/")({
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
    if (getActiveSessionState().userType !== SERVICE_PROVIDER) {
      throw redirect({ to: "/" });
    }
  },
  component: SpHome,
});

function SpHome() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: signOut, isPending: isSigningOut } = useLogout();

  const handleSignOut = async () => {
    await signOut();
    queryClient.clear();
    await navigate({ to: "/login" });
  };

  return (
    <Box className={terminalStyles.successContainer}>
      <Stack className={styles.content} gap={CONTENT_GAP}>
        <Image className={styles.logo} src="/favicon.svg" alt="" />
        <Text className={terminalStyles.successHeaderText}>{HEADER_TEXT}</Text>
        <Card className={styles.card}>
          <Text className={terminalStyles.successDescriptionText}>{NOTHING_HERE_TEXT}</Text>
        </Card>
        <Group justify="flex-end">
          <Button variant="secondary" onClick={handleSignOut} loading={isSigningOut}>
            Log out
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
